import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import VerificationSDK from "../VerificationSDK";
import "./kycModal.css";

interface KycFlowProps {
  sdk?: VerificationSDK;
  serverKey?: string;
  sessionId?: string;
  startAtQr?: boolean;
  pollIntervalMs?: number;
  onClose?: () => void;
  apiBaseUrl?: string;
  // If your mobile page is hosted on a different domain, set it here
  // e.g. "https://astra-sdk-testing.vercel.app"
  mobileBaseUrl?: string;
}

export const KycFlow: React.FC<KycFlowProps> = ({
  sdk: providedSdk,
  serverKey,
  sessionId: externalSessionId,
  startAtQr = true,
  pollIntervalMs = 20000,
  onClose,
  apiBaseUrl,
  mobileBaseUrl = "https://astra-sdk-testing.vercel.app"
}) => {
  console.log("-----------------------------------------")
  console.log("sdk",providedSdk)
  console.log('[KycFlow] render eterterte', {
    hasProvidedSdk: Boolean(providedSdk),
    hasServerKey: Boolean(serverKey),
    externalSessionId,
    startAtQr,
    apiBaseUrl
  });

  const [sdkInstance, setSdkInstance] = useState<VerificationSDK | null>(null);
  const [showQrScreen, setShowQrScreen] = useState<boolean>(startAtQr);
  const [waitingForMobile, setWaitingForMobile] = useState<boolean>(false);
  const [finalStatus, setFinalStatus] = useState<any>(null);

  // Initialize SDK instance properly
  useEffect(() => {
    console.log('[KycFlow] effect:initializeSdk', {
      providedSdk: Boolean(providedSdk),
      serverKey,
      externalSessionId,
      apiBaseUrl
    });

    if (providedSdk) {
      // Use provided SDK instance
      setSdkInstance(providedSdk);
      if (externalSessionId) {
        providedSdk.setSessionId(externalSessionId);
        try { localStorage.setItem("kyc_session_id", externalSessionId); } catch { }
      }
    } else if (serverKey) {
      // Create new SDK instance with serverKey
      const newSdk = new VerificationSDK({
        serverKey: serverKey,
        apiBaseUrl
      });
      setSdkInstance(newSdk);

      if (externalSessionId) {
        newSdk.setSessionId(externalSessionId);
        try { localStorage.setItem("kyc_session_id", externalSessionId); } catch { }
      }
    }
  }, [providedSdk, serverKey, apiBaseUrl, externalSessionId]);

  // Resume flow if face already completed
  useEffect(() => {
    if (!sdkInstance) return;

    try {
      const faceDone = localStorage.getItem("kyc_face_done");
      const sid = localStorage.getItem("kyc_session_id");
      console.log('[KycFlow] effect:resumeIfFaceDone', { faceDone, sid, hasSdk: Boolean(sdkInstance) });

      if (sid && faceDone === "1") {
        sdkInstance.setSessionId(sid);
        setShowQrScreen(false);
        setWaitingForMobile(true);
      }
    } catch (error) {
      console.error('Error resuming flow:', error);
    }
  }, [sdkInstance]);

  // Set external session ID when SDK instance is available
  useEffect(() => {
    if (!sdkInstance || !externalSessionId) return;

    console.log('[KycFlow] effect:setExternalSessionId', { externalSessionId });

    try {
      const currentSessionId = sdkInstance.getSessionId();
      if (!currentSessionId || currentSessionId !== externalSessionId) {
        sdkInstance.setSessionId(externalSessionId);
        try { localStorage.setItem("kyc_session_id", externalSessionId); } catch { }
      }

      if (startAtQr) {
        setShowQrScreen(true);
      }
    } catch (error) {
      console.error('Error setting session ID:', error);
    }
  }, [sdkInstance, externalSessionId, startAtQr]);

  if (!sdkInstance) {
    return (
      <div className="kyc-backdrop">
        <div className="kyc-container" style={{ background: "#0b0f17", color: "#fff" }}>
          <p>Initializing KYC session...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showQrScreen && (
        <QrStartScreen
          sdk={sdkInstance}
          serverKey={serverKey}
          pollIntervalMs={pollIntervalMs}
          onClose={onClose}
          mobileBaseUrl={mobileBaseUrl}
          onDone={(maybeResult) => {
            console.log('[KycFlow] onDoneFromQr', { hasResult: Boolean(maybeResult) });
            setShowQrScreen(false);
            if (maybeResult) {
              setFinalStatus(maybeResult);
              setWaitingForMobile(false);
            } else {
              setWaitingForMobile(true);
            }
          }}
        />
      )}

      {waitingForMobile && (
        <DesktopWaitingForMobile
          sdk={sdkInstance}
          onShowStatus={(st) => { setFinalStatus(st); setWaitingForMobile(false); }}
          pollIntervalMs={pollIntervalMs}
        />
      )}

      {finalStatus && (
        <FinalStatusScreen
          finalStatus={finalStatus}
          onBack={() => { setWaitingForMobile(true); setFinalStatus(null); }}
          onClose={() => { setFinalStatus(null); setWaitingForMobile(false); }}
        />
      )}
    </div>
  );
};

// Update QrStartScreen to properly handle server key
interface QrStartScreenProps {
  sdk: VerificationSDK;

  pollIntervalMs?: number;
  onDone: (result?: any) => void;
  onClose?: () => void;
  serverKey?: string;
  mobileBaseUrl: string;
}

const QrStartScreen: React.FC<QrStartScreenProps> = ({
  sdk,
  pollIntervalMs = 20000,
  onDone,
  onClose,
  serverKey,
  mobileBaseUrl
}) => {
  const sessionId = sdk.getSessionId() || localStorage.getItem("kyc_session_id");
  const [qrUrl, setQrUrl] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [sdkInstance, setSdkInstance] = useState<VerificationSDK | null>(null);


  console.log('[QrStartScreen] render', { sessionId, serverKey });

  // Generate QR URL with proper server key
  // In QrStartScreen component - Update the QR generation useEffect
  useEffect(() => {
    if (!sessionId) {
      console.log('[QrStartScreen] No session ID available');
      return;
    }

    // Get server key from props or SDK instance (optional)
    // const effectiveServerKey = (serverKey || (sdk as any).getServerKey?.()) as string | undefined;

    // @ts-ignore
    const effectiveServerKey = sdkInstance.getServerKey();

    const keyParam = effectiveServerKey ? `&key=${encodeURIComponent(effectiveServerKey)}` : '';
    // Build cross-domain mobile URL (hosted app)
    const mobileUrl = `${mobileBaseUrl.replace(/\/$/, '')}/mobile-kyc/${encodeURIComponent(sessionId)}?mode=face${keyParam}&apiBaseUrl=${encodeURIComponent("https://backenddevmain.astraprotocol.com")}`;

    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(mobileUrl)}`;

    setQrUrl(qrCodeUrl);
    setError(null);
  }, [sessionId, serverKey, sdk, mobileBaseUrl]);

  // Polling for status
  useEffect(() => {
    if (!sessionId) {
      console.log('[QrStartScreen] No session ID for polling');
      return;
    }

    // const effectiveServerKey = serverKey || sdk.getServerKey();
        // @ts-ignore
        const effectiveServerKey = sdkInstance.getServerKey();
    if (!effectiveServerKey) {
      console.error('[QrStartScreen] No server key for polling');
      return;
    }

    console.log('[QrStartScreen] Starting polling', { sessionId, pollIntervalMs });

    let id: number | null = null;
    const transientRef = { count: 0 };

    const poll = async () => {
      try {
        const status: any = await sdk.getStatus();
        transientRef.count = 0;
        setError(null);

        const data: any = status?.data ?? status;
        const faceStatus: string | undefined = data?.face_recognition_status || data?.face_status || data?.face || data?.faceRecognitionStatus;

        console.log('[QrStartScreen] poll:status', { faceStatus });

        if (typeof faceStatus === "string" && faceStatus.toUpperCase() === "COMPLETED") {
          try { localStorage.setItem("kyc_face_done", "1"); } catch { }
          onDone();
          return;
        }
      } catch (e: any) {
        const msg = e?.message ? String(e.message) : "Unknown error";
        console.log('[QrStartScreen] poll:error', msg);

        const isTransient = /unexpected token\s*</i.test(msg) ||
          /failed to fetch/i.test(msg) ||
          /networkerror/i.test(msg) ||
          /not a valid json/i.test(msg) ||
          /unexpected token/i.test(msg) ||
          /json parse/i.test(msg);

        if (msg && /expired/i.test(msg)) {
          toast.error(msg);
        } else if (isTransient) {
          transientRef.count += 1;
          if (transientRef.count >= 6) {
            toast.warning("Network problem while checking status — retrying...");
          }
        } else {
          toast.error(msg || "Error polling status");
        }
      }

      id = window.setTimeout(poll, Math.max(1000, pollIntervalMs));
    };

    poll();

    return () => {
      if (id) window.clearTimeout(id);
    };
  }, [sdk, onDone, serverKey, sessionId, pollIntervalMs]);

  // const effectiveServerKey = (serverKey || (sdk as any).getServerKey?.()) as string | undefined;
      // @ts-ignore
      const effectiveServerKey = sdkInstance.getServerKey();
      
  const mobileUrl = sessionId
    ? `${mobileBaseUrl.replace(/\/$/, '')}/mobile-kyc/${sessionId}?mode=face${effectiveServerKey ? `&key=${encodeURIComponent(effectiveServerKey)}` : ''}`
    : "";

  const handleManualRefresh = async () => {
    console.log('[QrStartScreen] manualRefresh');
    try {
      const result = await sdk.getResult();
      onDone(result);
    } catch (e: any) {
      toast.error(e?.message || "Unable to refresh status");
    }
  };

  const copyMobileUrl = async () => {
    if (!mobileUrl) return;
    try {
      await navigator.clipboard.writeText(mobileUrl);
      toast.success("URL copied to clipboard");
    } catch {
      const ta = document.createElement('textarea');
      ta.value = mobileUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success("URL copied to clipboard");
    }
  };

  return (
    <div className="kyc-backdrop">
      <div className="kyc-container" style={{ background: "#0b0f17", color: "#fff", position: "relative" }}>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8, background: "transparent", color: "#fff", border: "none", fontSize: 20, lineHeight: 1, cursor: "pointer" }}
        >
          ✕
        </button>

        <div style={{ textAlign: "center" }}>
          <h2 className="kyc-title" style={{ color: "#fff", marginBottom: 8 }}>Continue on Mobile</h2>
          <p style={{ margin: 0, color: "#9ca3af" }}>Scan this QR on your phone to capture your face and document</p>
        </div>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          {error ? (
            <p style={{ color: '#ef4444' }}>{error}</p>
          ) : qrUrl ? (
            <div style={{ display: 'inline-block', padding: 12, borderRadius: 12, border: '1px solid #334155', background: '#0b0f17' }}>
              <div style={{ background: '#ffffff', padding: 12, borderRadius: 8 }}>
                <img src={qrUrl} alt="Scan QR to continue on mobile" style={{ width: 220, height: 220, display: 'block' }} />
              </div>
            </div>
          ) : (
            <p style={{ color: '#9ca3af' }}>Preparing QR...</p>
          )}

          {mobileUrl && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
              <span>Or open: </span>
              <a href={mobileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'none' }}>
                {mobileUrl.substring(0, 30)}...
              </a>
              <button
                onClick={copyMobileUrl}
                title="Copy URL"
                style={{ marginLeft: 6, background: 'transparent', color: '#9ca3af', border: '1px solid #475569', borderRadius: 6, padding: '2px 6px', cursor: 'pointer' }}
              >
                📋
              </button>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              onClick={handleManualRefresh}
              style={{ padding: '12px 16px', width: '100%', maxWidth: 360, borderRadius: 8, border: 'none', background: 'linear-gradient(90deg, #FF8A00 0%, #FF3D77 100%)', color: '#ffffff', fontSize: 16, fontWeight: 600, cursor: 'pointer' }}
            >
              I've completed on mobile - Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add this helper component for final status
const FinalStatusScreen: React.FC<{
  finalStatus: any;
  onBack: () => void;
  onClose: () => void;
}> = ({ finalStatus, onBack, onClose }) => {
  const data: any = finalStatus?.data ?? finalStatus;
  const email: string | undefined = data?.email;
  const faceStatus: string = (String(data?.face_recognition_status || data?.face_status || data?.face || "Pending")).trim();
  const kycStatus: string = (String(data?.kyc_status || data?.status || "Pending")).trim();

  const pill = (label: string, value: string) => (
    <div style={{ background: "#111827", border: "1px solid #30363d", borderRadius: 12, padding: "14px 16px", color: "#e5e7eb", boxShadow: "0 8px 16px rgba(0,0,0,0.35)" }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      <span style={{ marginLeft: 8, color: label.toLowerCase().includes("status") ? "#f97316" : "#e5e7eb" }}>
        {value || "—"}
      </span>
    </div>
  );

  return (
    <div className="kyc-backdrop">
      <div className="kyc-container" style={{ background: "#0b0f17", color: "#fff", position: "relative" }}>
        <div style={{ position: "absolute", left: 12, top: 12, cursor: "pointer", fontSize: 20 }} onClick={onBack} aria-label="Back">←</div>
        <div style={{ position: "absolute", right: 12, top: 12, cursor: "pointer" }} onClick={onClose} aria-label="Close">✕</div>
        <h2 className="kyc-title" style={{ color: "#fff", textAlign: "center", marginBottom: 16 }}>Verification Status</h2>
        <div style={{ display: "grid", gap: 12 }}>
          {pill("Email:", email || "—")}
          {pill("Face Status:", faceStatus || "Pending")}
          {pill("KYC Status:", kycStatus || "Pending")}
        </div>
      </div>
    </div>
  );
};

// Keep the existing DesktopWaitingForMobile component as is
interface DesktopWaitingProps {
  sdk: VerificationSDK;
  onShowStatus: (status: any) => void;
  pollIntervalMs?: number;
}

const DesktopWaitingForMobile: React.FC<DesktopWaitingProps> = ({ sdk, onShowStatus, pollIntervalMs = 20000 }) => {
  const [message, setMessage] = useState<string>("After uploading your document on mobile, click Refresh to view status.");
  const [loading, setLoading] = useState(false);

  const checkNow = async () => {
    setLoading(true);
    try {
      const result = await sdk.getResult();
      onShowStatus(result);
    } catch (e: any) {
      setMessage("Still waiting for mobile to finish. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="kyc-backdrop">
      <div className="kyc-container" style={{ background: "#0b0f17", color: "#fff" }}>
        <h2 className="kyc-title" style={{ color: "#fff" }}>Continue on Mobile</h2>
        <p style={{ color: "#9ca3af", marginTop: 0 }}>Face is completed. Upload your document on mobile, then refresh.</p>
        <p style={{ color: "#9ca3af" }}>{message}</p>
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={checkNow}
            disabled={loading}
            className="kyc-input"
            style={{ background: "linear-gradient(90deg, #FF8A00 0%, #FF3D77 100%)", color: "#fff", border: "none", borderRadius: 8, padding: 10 }}
          >
            {loading ? "Checking..." : "I've completed on mobile - Refresh"}
          </button>
        </div>
      </div>
    </div>
  );
};